/**
 * list-filter-extra-fields — pola, po których serwer listy potrafi filtrować, choć NIE są
 * kolumnami tej listy (więc edytor „szybkich filtrów", który oferuje tylko kolumny, ich nie zna).
 *
 * Dziś: Opiekun i Przypisany kontrahenta — filtrowalne po stronie serwera
 * (`contractor-list-descriptor.ts`, `autoHeaderFilterColumns.include`), bez kolumny na liście.
 * Etykiety jak na KARCIE kontrahenta (katalog pól): „Opiekun" = `owner_user_id`, „Przypisany" =
 * `assigned_user_id`.
 * Slug pola = id w zapisanym filtrze = slug w `?headerFilters=` (nie ma tłumaczenia id→slug).
 */

export interface ExtraFilterField {
  /** Slug pola filtra (jak w `?headerFilters=`). */
  id: string;
  /** Klucz i18n etykiety. */
  labelKey: string;
  /** Pole niesie identyfikator użytkownika — edytor oferuje wtedy wstawienie `@userId`. */
  isUser?: boolean;
}

const EXTRA_FILTER_FIELDS: Record<string, readonly ExtraFilterField[]> = {
  contractors: [
    { id: 'owner_user_id', labelKey: 'tenant.listBuilder.qf.extraFields.ownerUser', isUser: true },
    {
      id: 'assigned_user_id',
      labelKey: 'tenant.listBuilder.qf.extraFields.assignedUser',
      isUser: true,
    },
  ],
};

/** Dodatkowe pola filtra dla listy o danym slugu kartoteki (pusta lista, gdy brak). */
export function getExtraFilterFields(slug: string): readonly ExtraFilterField[] {
  return EXTRA_FILTER_FIELDS[slug] ?? [];
}

/** Czy `fieldId` jest dodatkowym polem filtra tej listy. */
export function isExtraFilterField(slug: string, fieldId: string): boolean {
  return getExtraFilterFields(slug).some((f) => f.id === fieldId);
}

/**
 * Czy pole filtra (stałego) trzyma identyfikator UŻYTKOWNIKA — edytor pokazuje wtedy przycisk
 * `@userId`.
 *
 * Katalog pól nie mówi, na co wskazuje pole typu „odwołanie" (użytkownik, firma, kontrahent…),
 * więc rozpoznajemy po: typie pola `user` (pola modułów własnych) albo konwencji nazw kolumn
 * rdzenia — osoba w bazie to zawsze `<coś>_user_id` (`owner_user_id`, `assigned_user_id`,
 * `created_by_user_id`). Przycisk to wygoda; brak go przy polu niezgodnym z konwencją nie psuje
 * niczego — `@userId` można wtedy wpisać ręcznie.
 */
export function isUserFilterField(field: { id: string; fieldType?: string | null }): boolean {
  return field.fieldType === 'user' || /_user_id$/.test(field.id);
}
